import axios from "axios";
import { baseURL } from "../globals/constants";
import { showErrorToast } from "../utils/Toast";

const headers = () => ({ headers: { Accept: "application/json", Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
const url = `${baseURL}/v1/media`;
const fail = (error: any, fallback: string) => {
  showErrorToast(error?.response?.data?.message || fallback, false);
  return null;
};

export interface MediaAsset {
  id: string;
  key: string;
  type: "image" | "video";
  name: string;
  url: string;
  posterUrl?: string;
  contentType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  createdAt?: string;
}

export const getMedia = async (search = "", page = 1, limit = 40): Promise<{ results: MediaAsset[]; total: number }> => {
  try {
    const query = new URLSearchParams({ page: String(page), limit: String(limit), ...(search ? { search } : {}) });
    return (await axios.get(`${url}?${query}`, headers())).data;
  } catch (error: any) {
    fail(error, "Mediebiblioteket kunne ikke hentes");
    return { results: [], total: 0 };
  }
};

/** Lægger en fil op. Serveren skalerer billedet og skriver det om til et format, der virker alle steder. */
export const uploadMedia = async (file: File): Promise<MediaAsset | null> => {
  try {
    const form = new FormData();
    form.append("file", file);
    return (await axios.post(url, form, { headers: { ...headers().headers, "Content-Type": "multipart/form-data" } })).data;
  } catch (error: any) {
    return fail(error, "Filen kunne ikke lægges op");
  }
};

export const renameMedia = async (id: string, name: string): Promise<MediaAsset | null> => {
  try {
    return (await axios.patch(`${url}/${id}`, { name }, headers())).data;
  } catch (error: any) {
    return fail(error, "Navnet kunne ikke gemmes");
  }
};

/** Serveren nægter at slette et medie, der er i brug, og fortæller hvor det bruges */
export const deleteMedia = async (id: string): Promise<boolean> => {
  try {
    await axios.delete(`${url}/${id}`, headers());
    return true;
  } catch (error: any) {
    fail(error, "Mediet kunne ikke slettes");
    return false;
  }
};

export interface HelpVideo {
  slot: "landing" | "dashboard" | "createBreef" | "editBreef";
  active: boolean;
  label: string;
  title: string;
  mediaKey: string;
  mediaUrl: string;
  youtubeUrl: string;
}

export const getHelpVideos = async (): Promise<HelpVideo[]> => {
  try {
    return (await axios.get(`${url}/help-videos`, headers())).data?.results || [];
  } catch (error: any) {
    fail(error, "Hjælpevideoerne kunne ikke hentes");
    return [];
  }
};

export const saveHelpVideo = async (slot: string, patch: Partial<HelpVideo>): Promise<HelpVideo | null> => {
  try {
    return (await axios.patch(`${url}/help-videos/${slot}`, patch, headers())).data;
  } catch (error: any) {
    return fail(error, "Hjælpevideoen kunne ikke gemmes");
  }
};

/** Videoer går direkte til lageret (presign, PUT, complete) som breef-videoer; en ældre API får filen den gamle vej */
export const uploadVideo = async (file: File): Promise<MediaAsset | null> => {
  try {
    let presign: any = null;
    try {
      presign = (await axios.post(`${url}/video/presign`, { fileName: file.name, contentType: file.type || "video/mp4", sizeBytes: file.size }, headers())).data;
    } catch (error: any) {
      if ([400, 413].includes(error?.response?.status)) throw error;
      presign = null;
    }
    if (presign?.url) {
      await axios.put(presign.url, file, { headers: { "Content-Type": file.type || "video/mp4" } });
      return (await axios.post(`${url}/video/complete`, { key: presign.key, name: file.name, sizeBytes: file.size }, headers())).data;
    }
    const form = new FormData();
    form.append("videoFile", file);
    return (await axios.post(`${url}/video`, form, { headers: { ...headers().headers, "Content-Type": "multipart/form-data" } })).data;
  } catch (error: any) {
    return fail(error, "Videoen kunne ikke lægges op");
  }
};
